

  import Vue from 'vue'
  import Component from "vue-class-component";
  import {namespace} from "vuex-class";
  import {IOnOffHandlers} from "bonomo/dist";
  import {ILocker} from "@/model/Locker/lockerInterfaces";
  import {IShortServiceProvider} from "@/model/shortInterfaces";
  import ServiceProviderChip from "@/components/shared/chips/ServiceProviderChip.vue";
  import {Watch} from "vue-property-decorator";
  import {BetterDataTableHeader} from "@/model/betterVuetifyInterfaces";
  import LockerSearch from "@/components/locker/widget/LockerSearch.vue";
  import {EServiceProviderType} from "@/model/Service/serviceProviderInterfaces";
  import {IChipStyle} from "@/model/chipStyleInterfaces";
  import ServiceProviderTypeChip from "@/components/shared/chips/ServiceProviderTypeChip.vue";
  import {BoListPanel} from "bonomo/dist";
  import ServiceProviderActiveWidget from "@/components/serviceProvider/widget/ServiceProviderActiveWidget.vue";
  import BoListFilters from "@/components/shared/BoListFilters.vue";
  import LockerNav from "@/components/locker/widget/LockerNav.vue";

  const lockerModule = namespace("lockerModule");
  const serviceProviderModule = namespace("serviceProviderModule");
  const lockerServiceProviderModule = namespace('lockerServiceProviderModule');

  interface IProviderWithConnect extends IShortServiceProvider {
    connectModel: boolean;
    position_natural: number|null;
    channel_inconsistency: boolean;
  }
  interface IConnectLockerProviderRequest {
    lockerId: number;
    serviceProviderId: number;
    connect: boolean;
  }

  const POSITION_NATURAL = 'position_natural'

  @Component({
    components: {
      BoListFilters,
      LockerNav,
      ServiceProviderActiveWidget,
      BoListPanel,
      ServiceProviderTypeChip,
      LockerSearch,
      ServiceProviderChip,
    }
  })
  export default class LockersProviders extends Vue {

    @lockerServiceProviderModule.Action('fetchLockersProviderIds')
    private fetchLockersProviderIds!: (lockerId: number) => Promise<any>;
    @lockerServiceProviderModule.Action('connectLockerProvider')
    private connectLockerProvider!: (data: IConnectLockerProviderRequest) => Promise<any>;
    @lockerServiceProviderModule.Getter('lockersProviderIds')
    private connectedIds!: number[];
    @lockerServiceProviderModule.Getter('inconsistentProviderIds')
    private inconsistentIds!: number[];

    @lockerModule.Getter('lockers')
    private lockers!: ILocker[];

    @serviceProviderModule.Getter('serviceProviders')
    private providers!: IShortServiceProvider[];

    @Watch('$route')
    onUrlChanged($route: any) {
      this.loadData();
    }

    protected connectionsLoading: boolean = true;
    protected locker?: ILocker;
    protected lockerId: number = 0;
    protected maxPosition: number = 0;
    protected onlyActive: boolean = true;
    protected onlyAssigned: boolean = true;
    protected providerItems: IProviderWithConnect[] = [];
    protected search: string = '';
    protected sortBy: string | string[] = POSITION_NATURAL;
    protected sortDesc: boolean | boolean[] = false;
    protected subscribes: IOnOffHandlers = {};

    protected get headers(): BetterDataTableHeader[] {
      const ret: BetterDataTableHeader[] = [
        { text: this.$t('common.order'), align: 'start', value: POSITION_NATURAL },
        { text: this.$t('common.id'), align: 'start', sortable: true, value: 'id' },
        { text: this.$t('common.name'), align: 'start', value: 'name' },
        { text: this.$t('common.type'), align: 'start', sortable: true, value: 'type' },
        // { text: this.$t('services.provider.fields.address_town_city'), align: 'start', sortable: true, value: 'address_town_city' },
        { text: this.$t('common.active'), align: 'center', value: 'active', sortable: false },
        { text: this.$t('common.where'), align: 'center', value: 'shareCta', sortable: false },
        { text: this.$t('common.assigned'), align: 'center', value: 'connectModel', sortable: false },
      ];
      return ret.filter(each => (
          (each.value !== 'type' || this.$vuetify.breakpoint.mdAndUp) &&
          (each.value !== 'active' || this.$vuetify.breakpoint.lgAndUp) &&
          (each.value !== 'shareCta' || this.$vuetify.breakpoint.lgAndUp)
      ));
    }

    get combinedSearch() {
      // we make sure search is triggered even when search input is empty. otherwise our custom filter is not applied
      //  also A : B ensures that seach changes when the active/assigned switch changes
      return (this.search||'').toString() +
        (this.onlyAssigned ? 'A' : 'B') +
        (this.onlyActive ? 'A' : 'B');
    }

    created() {
      this.subscribes = this.$ons({
        'login.OK': this.loadData,
        'backend.call.OK.serviceProvidersLoaded': this.onProvidersLoaded,
        'backend.call.OK.connectionIdsLoaded': this.makeProviderItems,
        'backend.call.OK.lockersLoaded': this.onLockersLoaded,
        'backend.call.OK.listServiceProviderLoaded': this.onActiveUpdated,
      });
    }

    beforeDestroy() {
      this.$offs(this.subscribes);
    }

    private loadData(initial: boolean = false) {
      // load only if it's a reload (not initial), or if there are no lockers
      if (!initial || !this.lockers || !this.lockers.length) {
        this.$store.dispatch('lockerModule/fetchLockers');
      }
      else {
        this.onLockersLoaded();
      }
      if (!initial || !this.providers || !this.providers.length) {
        this.$store.dispatch('serviceProviderModule/fetchServiceProviders');
      }
      else {
        this.onProvidersLoaded();
      }
    }

    protected customSort(items: IProviderWithConnect[], indexList: string[], isDesc: boolean[]) {
      items.sort((a: IProviderWithConnect, b: IProviderWithConnect) => {
        if (a.connectModel && !b.connectModel) return -1;
        if (b.connectModel && !a.connectModel) return 1;
        // this code tries to support multi-column sorting but effectively only keeps the last value as acc is ignored
        // (2 other issues: we don't support multi-column sorting so we can ignore it.
        //  also, we cannot calculate non-0 compare values and then sum them with reduce
        //  which probably was the original intent
        // if they both have position or don't, then we need to take care of the comparison
        // return indexList.reduce((acc: number, index: string): number => {
        //   return this.compare(a, b, index, isDesc[indexList.indexOf(index)]);
        // }, 0);
        return indexList
          .map((index: string): number => {
            return this.compare(a, b, index, isDesc[indexList.indexOf(index)]);
          })
          // we could use reduce like this to look for the first non 0 value but it will fail when
          .reduce((current: number, next: number): number => current !== 0 ? current : next, 0);
      });
      return items;
    }

    private compare(a: IProviderWithConnect, b: IProviderWithConnect, index: string, isDesc: boolean): number {
      let elA: IProviderWithConnect = a;
      let elB: IProviderWithConnect = b;

      const strcmp = (a: string, b: string): number => a === b ? 0 : (a < b ? -1 : 1);

      if (isDesc) {
        elA = b;
        elB = a;
      }

      switch (index) {
        case 'position_natural': {
          return elA['position_natural'] && elB['position_natural']
              ? elA['position_natural'] - elB['position_natural']
              : strcmp(elA['name'], elB['name']); // this applies a 2nd sort by name for not assigned providers
        }
        default: {
          return strcmp((elA as any)[index], (elB as any)[index]);
        }
      }

    }

    private makeProviderItems() {
      this.providerItems.forEach(
          (each) => {
            each.connectModel = this.hasProvider(each.id);
            each.position_natural = this.providerOrder(each.id);
            each.channel_inconsistency = this.inconsistentIds.indexOf(each.id) !== -1;
          }
      );
      this.connectionsLoading = false;
    }

    protected providersFilter(
      value: any,
      search: string,
      item: IProviderWithConnect
    ) {
      if (item.connectModel) {
        return true;
      }
      const notBySearch = this.search.length &&
        (value||'').toString().toLowerCase().indexOf(this.search.toLowerCase()) === -1
      const notByActive = this.onlyActive && !item.active;
      const notByAssigned = this.onlyAssigned && !item.connectModel;
      const notByDeleted = item.deleted_at && !item.connectModel;
      return !notBySearch && !notByActive && !notByAssigned && !notByDeleted;
    }

    private hasProvider(providerId: number) {
      return this.connectedIds && (this.connectedIds.indexOf(providerId) !== -1);
    }

    private onLockerSelected(lockerId: number) {
      this.connectionsLoading = true;
      this.lockerId = lockerId;
      this.locker = this.lockers.find((each) => each.id == this.lockerId);
      if (this.$route.params.lockerId != (''+lockerId)) {
        this.$router.push({name: 'lockersServiceProviders', params: {lockerId: ''+lockerId}});
      }
      Vue.nextTick(
          () => this.fetchLockersProviderIds(this.lockerId)
      );
    }

    private onLockersLoaded() {
      Vue.nextTick(() => this.onLockerSelected(
          parseInt(this.$route.params.lockerId) || 0
      ));
    }

    protected onMoveUp(item: IProviderWithConnect) {
      this.connectionsLoading = true;
      this.$store.dispatch('lockerServiceProviderModule/moveUp', {lockerId: this.lockerId, serviceProviderId: item.id});
    }

    protected onMoveDown(item: IProviderWithConnect) {
      this.connectionsLoading = true;
      this.$store.dispatch('lockerServiceProviderModule/moveDown', {lockerId: this.lockerId, serviceProviderId: item.id});
    }

    private onProvidersLoaded() {
      this.maxPosition = 0;
      this.providerItems = this.providers.map(
          each => Object.assign({}, each, { connectModel: false, position_natural: null, channel_inconsistency: false })
      );
      this.makeProviderItems();
    }

    protected onSortBy(v: string) {
      this.sortBy = v;
    }

    protected onSortDesc(v: boolean) {
      this.sortDesc = v;
    }

    protected onSwitch(serviceProviderId: number, connect: boolean) {
      this.connectionsLoading = true;
      this.connectLockerProvider({
        lockerId: this.lockerId,
        serviceProviderId,
        connect
      });
    }

    private onActiveUpdated(serviceProvider: IShortServiceProvider) {
      this.providerItems.forEach(each => {
        if (each.id == serviceProvider.id) {
          each.active = serviceProvider.active;
        }
      });
    }

    private providerOrder(providerId: number): number|null {
      if (this.connectedIds) {
        let providerOrder = this.connectedIds.indexOf(providerId);
        this.maxPosition = Math.max(this.maxPosition, providerOrder + 1);
        return providerOrder < 0 ? null : providerOrder + 1;
      }

      return null;
    }

    protected isRestricted(item: IProviderWithConnect): boolean {
      const limitedTo = item.limited_to;
      if (!limitedTo || !limitedTo.length || !this.locker) return false;
      const communityId = this.locker.community_id;
      if (!communityId) return false;
      return !limitedTo.map(Number).includes(communityId);
    }

    protected showMovers(item: IProviderWithConnect): boolean {
      return !this.sortDesc && this.sortBy === POSITION_NATURAL && !!item[POSITION_NATURAL];
    }

  }
